import clsx from 'clsx';
import RichText from 'components/ui/ContentBlocks/RichText';
import FunnelBlock from 'components/ui/FunnelBlock';
import Highlight from 'components/ui/Highlight';
import tailwind from 'utils/breakpoints';
import { useLivePreview } from 'hooks/useLivePreview';
import styles from './Banner.module.scss';
import type { InspectorPropsType } from 'hooks/useLivePreview';
import type {
  IBlockBanner,
  IBlockBannerFields,
} from 'types/contentful/generated';

type Props = IBlockBannerFields &
  Partial<IBlockBanner> & {
    marginBottom?: boolean;
    sidebar?: boolean;
    config?: Record<string, unknown>;
    inspectorProps?: InspectorPropsType;
    __typename?: string;
  };

const Banner = ({
  blockId,
  backgroundImage,
  highlight1,
  highlight2,
  highlight3,
  marginBottom = true,
  preTitle,
  theme = 'turquoise background',
  title,
  sidebar = false,
  funnel,
  funnelPosition = 'left',
  text,
  config,
  ...props
}: Props): JSX.Element => {
  const { updatedEntity, inspectorProps } = useLivePreview({
    sys: props.sys,
    __typename: props.__typename,
    title,
    preTitle,
  });
  let bgImageWidth = '';
  if (backgroundImage) {
    if (backgroundImage?.fields) {
      bgImageWidth =
        backgroundImage.fields.file.details.image?.width &&
        backgroundImage.fields.file.details.image.width > 1000
          ? '?w=1000'
          : `?w=${backgroundImage.fields.file.details.image?.width}`;
    } else {
      bgImageWidth =
        // eslint-disable-next-line
        // @ts-ignore
        backgroundImage.width && backgroundImage.width > 1000
          ? '?w=1000'
          : // eslint-disable-next-line
            // @ts-ignore
            `?w=${backgroundImage.width}`;
    }
  }

  let themeConfig: {
    background?: string;
    preTitle?: string;
    headline?: string;
    highlight?: string;
    iconStrokeColor?: string;
    iconColor?: string;
  } = {};

  switch (theme) {
    case 'light':
    case 'yellow background':
      themeConfig = {
        background: 'bg-secondary',
        preTitle: 'text-white',
        headline: 'text-navy',
        highlight: 'text-navy',
        iconStrokeColor: 'text-navy',
        iconColor: tailwind.theme.backgroundColor.navy,
      };

      break;
    case 'dark':
    case 'turquoise background':
      themeConfig = {
        background: 'bg-navy',
        preTitle: 'text-tertiary',
        headline: 'text-white',
        highlight: 'text-white',
        iconStrokeColor: 'text-tertiary',
        iconColor: tailwind.theme.backgroundColor.secondary,
      };

      break;
    case 'neutral background':
      themeConfig = {
        background: 'bg-blueTurquoise',
        preTitle: 'text-navy',
        headline: 'text-navy',
        highlight: 'text-navy',
        iconStrokeColor: 'text-navy',
        iconColor: tailwind.theme.backgroundColor.navy,
      };

      break;
  }

  if (funnel && funnel.fields) {
    delete funnel.fields.fullWidth;
  }

  // eslint-disable-next-line
  // @ts-ignore
  if (funnel && funnel.fullWidth) {
    // eslint-disable-next-line
    // @ts-ignore
    delete funnel.fullWidth;
  }

  const hasHighlight = highlight1 || highlight2 || highlight3;
  const useH1 = (config && (config.useH1 as boolean)) || false;

  const getStyle = (): object => {
    if (backgroundImage) {
      return {
        backgroundImage: `url(${
          backgroundImage.fields
            ? backgroundImage.fields.file.url
            : /** eslint-disable-next-line
               * @ts-ignore */
              backgroundImage.url
        }${bgImageWidth})`,
      };
    }

    return {};
  };

  return (
    <>
      <div
        className={clsx(
          `banner-${blockId} bg-cover bg-center bg-no-repeat`,
          marginBottom && 'mb-8',
          themeConfig.background,
        )}
        style={getStyle()}
      >
        {title && (
          <div
            className={clsx(
              config?.bannerContainerSize
                ? (config.bannerContainerSize as string)
                : 'max-w-screen-xl',
              'mx-auto px-5 h-full md:px-4',
              sidebar ? 'flex items-center' : 'py-10',
              !sidebar &&
                useH1 && [
                  'md:pt-16',
                  hasHighlight && !funnel
                    ? 'md:pb-6'
                    : [funnel ? 'md:pb-20' : 'md:pb-16'],
                ],
            )}
          >
            <div className="md:grid md:grid-cols-6 md:gap-x-5 pb-6">
              {funnel && funnelPosition === 'left' && (
                <FunnelBlock funnelPosition="left" funnel={funnel} />
              )}
              <div
                className={clsx(
                  funnel ? 'md:col-span-3' : 'md:col-span-4',
                  !sidebar &&
                    !funnel &&
                    !config?.isLandingPage &&
                    'md:col-start-2',
                )}
              >
                {updatedEntity.preTitle && (
                  <p
                    className={clsx(
                      'uppercase font-bold mb-2 md:mb-1 text-lg',
                      themeConfig.preTitle,
                    )}
                    {...inspectorProps?.({ fieldId: 'preTitle' })}
                  >
                    {updatedEntity.preTitle}
                  </p>
                )}

                {useH1 ? (
                  <h1
                    className={clsx(
                      'text-4_5xl pt-0 leading-tight',
                      hasHighlight && !text && 'mb-10',
                      themeConfig.headline,
                    )}
                    {...inspectorProps?.({ fieldId: 'title' })}
                  >
                    {updatedEntity.title}
                  </h1>
                ) : (
                  <h2
                    className={clsx(
                      'text-4_5xl leading-tight pt-0',
                      text ? 'mb-4' : 'mb-10',
                      themeConfig.headline,
                    )}
                    {...inspectorProps?.({ fieldId: 'title' })}
                  >
                    {updatedEntity.title}
                  </h2>
                )}

                {text && (
                  <div className="text-white text-lg mb-6 leading-8 md:leading-6 white-link">
                    <RichText fields={text} />
                  </div>
                )}

                {highlight1 && (
                  <Highlight
                    wrapperClasses={clsx(themeConfig.highlight, 'md:mb-2')}
                    highlight={highlight1}
                    iconColor={themeConfig.iconColor}
                    iconClasses={clsx(
                      'stroke-current stroke-2',
                      themeConfig.iconStrokeColor,
                    )}
                    inspectorProps={{
                      ...inspectorProps?.({ fieldId: 'highlight1' }),
                    }}
                  />
                )}
                {highlight2 && (
                  <Highlight
                    wrapperClasses={clsx(themeConfig.highlight, 'md:mb-2')}
                    highlight={highlight2}
                    iconColor={themeConfig.iconColor}
                    iconClasses={clsx(
                      'stroke-current stroke-2',
                      themeConfig.iconStrokeColor,
                    )}
                    inspectorProps={{
                      ...inspectorProps?.({ fieldId: 'highlight2' }),
                    }}
                  />
                )}
                {highlight3 && (
                  <Highlight
                    wrapperClasses={clsx(themeConfig.highlight, 'md:mb-2')}
                    highlight={highlight3}
                    iconColor={themeConfig.iconColor}
                    iconClasses={clsx(
                      'stroke-current stroke-2',
                      themeConfig.iconStrokeColor,
                    )}
                    inspectorProps={{
                      ...inspectorProps?.({ fieldId: 'highlight3' }),
                    }}
                  />
                )}
              </div>
              {funnel && funnelPosition === 'right' && (
                <FunnelBlock funnelPosition="right" funnel={funnel} />
              )}
            </div>
          </div>
        )}
      </div>
      {funnel && (
        <div className={styles.funnelBlockWrapper}>
          <FunnelBlock
            wrapperClasses={styles.funnelWrapperClasses}
            funnelClasses={styles.funnelFunnelClasses}
            funnel={funnel}
            showHeadline
          />
        </div>
      )}
    </>
  );
};

export default Banner;
